import { useState } from 'react';
import { Stack } from '@mui/material';
import { useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import type { AdminApiCredentialListFilters, IssuedApiCredentialMaterial } from '@/api/models';
import { FiltersPopover } from '@/components/shell/FiltersPopover';
import { PageTitle } from '@/components/shell/PageTitle';
import { SectionHeader } from '@/components/shell/SectionHeader';
import { useCanManageAccess } from '@/access/useCanManageAccess';
import { countActiveCredentialFilters } from './filters/activeFilterCount';
import { CredentialsFiltersForm } from './filters/CredentialsFiltersForm';
import { CredentialsListView } from './CredentialsListView';
import { CredentialCreateDialog } from './CredentialCreateDialog';
import { CredentialSecretRevealPanel } from './CredentialSecretRevealPanel';

const DEFAULT_FILTERS: AdminApiCredentialListFilters = { page: 1, pageSize: 50 };
const PAGINATION_ONLY_KEYS = new Set(['page', 'pageSize']);

/**
 * API credentials surface against `/v1/admin/api-credentials` (A3's
 * contract). `platform:auditor` sees the same list read-only — Create is
 * hidden via `useCanManageAccess`. After a successful create, the once-only
 * secret pair (D-N) is shown in `CredentialSecretRevealPanel`; it lives
 * only in this page's own `issued` state and is cleared the instant the
 * panel closes, then the admin lands on the new credential's detail page.
 */
export function CredentialsPage() {
  const translate = useTranslate();
  const navigate = useNavigate();
  const canWrite = useCanManageAccess();
  const [filters, setFilters] = useState<AdminApiCredentialListFilters>(DEFAULT_FILTERS);
  const [refreshToken, setRefreshToken] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [issued, setIssued] = useState<IssuedApiCredentialMaterial>();

  function updateFilters(patch: Partial<AdminApiCredentialListFilters>) {
    const isPaginationOnly = Object.keys(patch).every((key) => PAGINATION_ONLY_KEYS.has(key));
    setFilters((prev) => ({ ...prev, ...patch, page: isPaginationOnly ? (patch.page ?? prev.page) : 1 }));
  }

  function handleCreated(credential: IssuedApiCredentialMaterial) {
    setCreateOpen(false);
    setRefreshToken((token) => token + 1);
    setIssued(credential);
  }

  function handleRevealClosed() {
    const id = issued?.id;
    setIssued(undefined);
    if (id) navigate(`/credentials/${encodeURIComponent(id)}`);
  }

  return (
    <Stack spacing={1.5}>
      <SectionHeader
        left={<PageTitle title={translate('pay.credentials.pageTitle')} subtitle={translate('pay.credentials.pageSubtitle')} />}
        filters={
          <FiltersPopover
            activeCount={countActiveCredentialFilters(filters)}
            onClear={() => setFilters(DEFAULT_FILTERS)}
          >
            <CredentialsFiltersForm filters={filters} onChange={updateFilters} />
          </FiltersPopover>
        }
        onRefresh={() => setRefreshToken((token) => token + 1)}
        onCreate={canWrite ? () => setCreateOpen(true) : undefined}
        createLabel={translate('pay.credentials.list.createLabel')}
      />
      <CredentialsListView key={refreshToken} filters={filters} onFiltersChange={updateFilters} />
      <CredentialCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} />
      <CredentialSecretRevealPanel
        open={Boolean(issued)}
        title={translate('pay.credentials.secretPanel.createdTitle')}
        secret={issued?.secret}
        signingSecret={issued?.signingSecret}
        onClose={handleRevealClosed}
      />
    </Stack>
  );
}
